import { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import type { OwnerBootstrapResult, OwnerSession } from '@ai-voice/contracts';
import { ApiError, api, saveOwnerSession } from '../api';
import { t, tError } from '../i18n';

type Props = {
  onAuthenticated: (session: OwnerSession) => void;
};

export function OwnerGate({ onAuthenticated }: Props) {
  const [initialized, setInitialized] = useState<boolean | null>(null);
  const [showBootstrap, setShowBootstrap] = useState(false);
  const [bootstrap, setBootstrap] = useState<OwnerBootstrapResult | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [login, setLogin] = useState({ username: 'owner', password: '', totp: '' });
  const [setup, setSetup] = useState({ bootstrapToken: '', username: 'owner', password: '' });

  useEffect(() => {
    api.ownerStatus()
      .then((result) => setInitialized(result.initialized))
      .catch((cause) => setError(cause instanceof Error ? cause.message : String(cause)));
  }, []);

  async function submitLogin(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const session = await api.login(login);
      saveOwnerSession(session);
      onAuthenticated(session);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  async function submitBootstrap(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const result = await api.bootstrap(setup);
      saveOwnerSession(result.session);
      setBootstrap(result);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  if (initialized === null) {
    return <section className="panel centered"><span className="spinner" />{t('正在检查管理服务...')}</section>;
  }

  if (bootstrap) {
    return (
      <section className="panel setup-panel">
        <div className="eyebrow">{t('一次性初始化')}</div>
        <h2>{t('保存你的双重验证信息')}</h2>
        <p className="muted">{t('下面的二维码和恢复码只显示这一次。恢复码建议打印或保存在密码管理器中。')}</p>
        <div className="setup-grid">
          <div className="qr-card">
            <QRCodeSVG value={bootstrap.otpauthUrl} size={184} bgColor="#ffffff" fgColor="#07111f" />
            <code>{bootstrap.totpSecret}</code>
          </div>
          <div className="recovery-card">
            <strong>{t('恢复码')}</strong>
            <div className="recovery-grid">
              {bootstrap.recoveryCodes.map((code) => <code key={code}>{code}</code>)}
            </div>
          </div>
        </div>
        <button className="primary" onClick={() => onAuthenticated(bootstrap.session)}>{t('我已安全保存，进入主控台')}</button>
      </section>
    );
  }

  return (
    <section className="gate-grid">
      <div className="hero-copy">
        <span className="eyebrow">{t('足球播报语音室')}</span>
        <h1>{t('把电脑里的声音，安全地交给指定的人听。')}</h1>
        <p>{t('浏览器直接共享系统声音。每张激活码最多绑定 3 台设备，同一时间只允许 1 台在线，有效期由主控设置。')}</p>
        <div className="feature-pills">
          <span>{t('系统声音')}</span><span>{t('三设备上限')}</span><span>{t('单设备在线')}</span><span>{t('完整审计')}</span>
        </div>
      </div>
      <div className="panel auth-card">
        {initialized ? (
          <>
            <div className="section-heading">
              <div><span className="eyebrow">{t('主控登录')}</span><h2>{t('管理员身份验证')}</h2></div>
            </div>
            <form onSubmit={submitLogin} className="stack">
              <label>{t('管理员账号')}<input value={login.username} onChange={(event) => setLogin({ ...login, username: event.target.value })} autoComplete="username" /></label>
              <label>{t('强密码')}<input type="password" value={login.password} onChange={(event) => setLogin({ ...login, password: event.target.value })} autoComplete="current-password" /></label>
              <label>{t('动态码或恢复码')}<input value={login.totp} onChange={(event) => setLogin({ ...login, totp: event.target.value })} autoComplete="one-time-code" placeholder={t('6 位动态码')} /></label>
              <button className="primary" disabled={busy}>{busy ? t('验证中...') : t('登录主控台')}</button>
            </form>
          </>
        ) : (
          <>
            <div className="section-heading">
              <div><span className="eyebrow">{t('首次部署')}</span><h2>{t('创建唯一管理员')}</h2></div>
            </div>
            {!showBootstrap ? (
              <div className="empty-state"><p>{t('系统尚未初始化。')}</p><button className="secondary" onClick={() => setShowBootstrap(true)}>{t('开始初始化')}</button></div>
            ) : (
              <form onSubmit={submitBootstrap} className="stack">
                <label>{t('部署引导令牌')}<input type="password" value={setup.bootstrapToken} onChange={(event) => setSetup({ ...setup, bootstrapToken: event.target.value })} /></label>
                <label>{t('管理员账号')}<input value={setup.username} onChange={(event) => setSetup({ ...setup, username: event.target.value })} /></label>
                <label>{t('强密码（至少 12 位）')}<input type="password" value={setup.password} onChange={(event) => setSetup({ ...setup, password: event.target.value })} /></label>
                <button className="primary" disabled={busy}>{busy ? t('创建中...') : t('创建管理员')}</button>
              </form>
            )}
          </>
        )}
        {error && <div className="alert error">{error}</div>}
      </div>
    </section>
  );
}

function errorMessage(cause: unknown): string {
  if (cause instanceof ApiError) return tError(cause.code, cause.message);
  return cause instanceof Error ? cause.message : String(cause);
}

